import { useAppSelector } from '@hooks/app/redux';
import { getNextPageParam } from '@lib/utils/reactQuery/infinityQueryHelpers';
import { getMedicalRecords } from '@services/external/medicalRecords.api';
import React from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';

import { GetMedicalRecordsParams } from 'types/medicalRecord.types';

const PAGE_SIZE = 20;

type QParams = Omit<GetMedicalRecordsParams, 'page' | 'pageSize'> & { providerId: string };

async function handler(page: number = 1, params: QParams) {
  const resData = await getMedicalRecords(params.providerId, {
    page,
    pageSize: PAGE_SIZE,
    ...params,
  });

  return resData.data.data;
}

type TQueryData = Awaited<ReturnType<typeof handler>>;

function useMedicalRecordsList(
  clientId: string,
  params: Omit<QParams, 'providerId' | 'clientId'> = { direction: 'DESC', sortBy: 'createdAt' },
  options?: Omit<UseInfiniteQueryOptions<TQueryData, any, TQueryData, TQueryData, any>, 'queryKey'>,
) {
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { data, ...rest } = useInfiniteQuery(
    ['medicalRecords:list', params, providerId, clientId],
    ({ pageParam }) => handler(pageParam, { ...params, providerId, clientId }),
    {
      ...options,
      getNextPageParam: getNextPageParam,
    },
  );

  const flattenData = React.useMemo(() => {
    return data?.pages.flatMap((p) => p.items) ?? [];
  }, [data]);

  return { data: flattenData, original: data, ...rest };
}

export default useMedicalRecordsList;
